/**
 * Conciliação de pagamentos (Task 16, brief 3.8).
 *
 * Leia isto antes de mexer:
 *
 * 1. **A tela não concilia; ela MOSTRA a conciliação.** O veredito
 *    concorda/diverge vem da regra pura (`src/financial/managerial.ts`,
 *    calculada no servidor); a tela lista e navega.
 * 2. **Divergência navega até o pedido.** Cada linha leva ao pedido unificado
 *    que a originou — mesmos registros, mesmo histórico (D-03).
 * 3. **Fonte ausente não é "tudo conciliado".** Sem a rota a tela declara a
 *    ausência; e quando os números vêm do simulador (credencial do adquirente
 *    ausente) a tela diz isso, nunca passa simulador por real.
 * 4. **Gerencial, nunca fiscal.** Nada aqui emite nota nem move dinheiro.
 *
 * Migrado para `.tsx`/JSX real (Cluster E — Financeiro & Sistema, redesign
 * por perfil; ver `docs/architecture/ADR-0011-*.md`): troca mecânica de
 * sintaxe (`createElement` → JSX). Esta tela já estava tokenizada
 * (`rounded-panel`/`border-border`/`bg-surface`/`shadow-s1`) e já usava
 * ícones `lucide-react` no selo de veredito antes desta frente — nenhum
 * ícone novo foi necessário, nenhum `data-*`/aria/texto/rota muda.
 */
// Import explícito de `React` — ver o mesmo comentário em `KpiCard.tsx`.
import React, { type ReactElement } from "react";
import { PageHeader, StatTile } from "../ui/index.js";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, Landmark, type LucideIcon, TriangleAlert } from "lucide-react";
import { RecordTable } from "../components/RecordTable.js";
import { FinancialBoundary } from "../components/FinancialBoundary.js";
import { StatusPanel } from "../components/StatusPanel.js";
import { isFinancialSourceAwaitingContract, isFinancialFromSimulator } from "../financial/dataset.js";
import { RECONCILIATION_STATE_LABELS, type ReconciliationState } from "../financial/managerial.js";
import { AWAITING_CONTRACT_LABEL } from "../dashboard/kpis.js";
import { formatBrl } from "../format.js";
import type { FinancialDataset } from "../financial/dataset.js";
import type { FinancialViewProps } from "../financial/view-props.js";
import type { FinancialReconciliation } from "../api/types.js";

/**
 * Selo de veredito da conciliação (ícone + texto, nunca só cor — régua §2).
 * `matched` concorda (success/check), `diverged` alerta (danger/triângulo),
 * `pending` aguarda (neutro/relógio). `data-reconciliation-status` segue no
 * wrapper (hook preservado dos testes). Exportado para a tela de detalhe reusar.
 */
const RECONCILIATION_BADGE_META: Record<ReconciliationState, { tone: string; Icon: LucideIcon }> = {
  matched: { tone: "border-success text-success", Icon: CheckCircle2 },
  diverged: { tone: "border-danger text-danger", Icon: TriangleAlert },
  pending: { tone: "border-border-strong text-body", Icon: Clock }
};

export function ReconciliationBadge({ status }: { status: ReconciliationState }): ReactElement {
  const meta = RECONCILIATION_BADGE_META[status];
  return (
    <span
      className={"inline-flex items-center gap-1.5 rounded-pill border bg-surface px-2 py-0.5 text-xs font-semibold " + meta.tone}
      data-reconciliation-status={status}
    >
      <meta.Icon className="h-3.5 w-3.5 shrink-0" aria-hidden focusable={false} />
      <span>{RECONCILIATION_STATE_LABELS[status]}</span>
    </span>
  );
}

/** Nota que declara quando os números vêm do simulador (credencial ausente). */
export function SimulatorNote({ financial }: { financial: FinancialDataset }): ReactElement | null {
  if (!isFinancialFromSimulator(financial)) return null;
  return (
    <p className="page__note text-sm text-muted" data-financial-source="simulator">
      Taxas do adquirente vindas do simulador: a credencial MERCADO_PAGO_ACCESS_TOKEN não está configurada neste
      ambiente. Os números são determinísticos (verde em código, aguardando secret) — nunca apresentados como repasse real.
    </p>
  );
}

export function ConciliacaoTable({ rows }: { rows: FinancialReconciliation[] }): ReactElement {
  return (
    <RecordTable<FinancialReconciliation>
      bare
      caption="Conciliações de pagamento, com bruto, deduções e veredito"
      rows={rows}
      idOf={(row) => row.id}
      emptyMessage="Nenhuma conciliação neste recorte."
      columns={[
        {
          key: "order",
          label: "Pedido",
          render: (row) => (
            <span data-order-external-id={row.orderExternalId ?? ""}>
              <Link to={`/pedidos/${row.platformOrderId}`} title="Abrir o pedido que originou a conciliação" className="font-semibold text-brand-text hover:underline">
                {row.orderExternalId ?? row.platformOrderId}
              </Link>
            </span>
          )
        },
        { key: "gross", label: "Bruto", render: (row) => formatBrl(row.grossCents) },
        { key: "fee", label: "Taxa", render: (row) => formatBrl(row.paymentFeeCents) },
        { key: "commission", label: "Comissão", render: (row) => formatBrl(row.commissionCents) },
        { key: "cost", label: "Custo", render: (row) => formatBrl(row.costCents) },
        { key: "expected", label: "Líquido esperado", render: (row) => formatBrl(row.expectedNetCents) },
        {
          key: "actual",
          label: "Repasse efetivo",
          render: (row) => (row.actualNetCents === null ? "aguardando repasse" : formatBrl(row.actualNetCents))
        },
        {
          key: "status",
          label: "Situação",
          render: (row) => <ReconciliationBadge status={row.status} />
        },
        {
          key: "detail",
          label: "Detalhe",
          render: (row) => (
            <Link to={`/financeiro/conciliacao/${row.id}`} className="font-semibold text-brand-text hover:underline">
              abrir
            </Link>
          )
        }
      ]}
    />
  );
}

export function ConciliacaoView({ financial }: FinancialViewProps): ReactElement {
  if (isFinancialSourceAwaitingContract(financial)) {
    return (
      <div className="page page--conciliacao flex flex-col gap-6" data-page="conciliacao">
        <PageHeader title="Conciliação de pagamentos" />
        <StatusPanel
          kind="awaiting_source"
          title={`Conciliação ${AWAITING_CONTRACT_LABEL}`}
          detail="A rota /api/platform/financial/summary não respondeu neste ambiente. As conciliações aparecem quando a rota for publicada — exibir 'tudo conciliado' diria, falsamente, que não há divergência a tratar."
        />
      </div>
    );
  }
  const { reconciliations, summary } = financial;
  const divergedCount = summary.diverged;
  return (
    <div className="page page--conciliacao flex flex-col gap-6" data-page="conciliacao">
      <PageHeader
        title="Conciliação de pagamentos"
        description="Compara o repasse efetivo com o líquido esperado de cada pedido (bruto menos taxa, comissão e custo)."
      />
      <p className="sr-only" data-reconciliation-count={reconciliations.length}>
        {`${reconciliations.length} conciliação(ões): ${summary.reconciled} concordam, ${divergedCount} divergem, ${summary.pending} aguardando repasse.`}
      </p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile label="Conciliações" value={reconciliations.length} icon={Landmark} />
        <StatTile label="Concordam" value={summary.reconciled} icon={CheckCircle2} />
        <StatTile label="Divergem" value={divergedCount} icon={TriangleAlert} />
        <StatTile label="Aguardando repasse" value={summary.pending} icon={Clock} />
      </div>
      <SimulatorNote financial={financial} />
      <p className={"m-0 rounded-panel border px-4 py-3 text-sm " + (divergedCount === 0 ? "border-border bg-surface text-muted" : "border-warning bg-warning-soft text-ink")} data-reconciliation-diverged={divergedCount}>
        {divergedCount === 0 ? (
          "Nenhuma divergência em aberto."
        ) : (
          <span>
            {`Divergências (${divergedCount}). `}
            <Link to="/financeiro/conciliacao?status=diverged" className="page__note-link font-semibold text-brand-text hover:underline">
              ver só as que divergem
            </Link>
          </span>
        )}
      </p>
      <p className="page__note text-sm text-muted">
        A conciliação LÊ o pagamento já registrado e aponta a diferença entre o repasse e o líquido esperado (bruto
        menos taxa, comissão e custo). Ela não executa pagamento nem estorno — gerencial, não fiscal.
      </p>
      <section className="flex flex-col gap-3 rounded-panel border border-border bg-surface p-5 shadow-s1">
        <h3 className="text-lg font-extrabold text-ink">Conciliações</h3>
        <ConciliacaoTable rows={reconciliations} />
      </section>
    </div>
  );
}

export function Conciliacao(): ReactElement {
  const [searchParams] = useSearchParams();
  const statusParam = searchParams.get("status");
  const onlyDiverged = statusParam === "diverged";
  return (
    <FinancialBoundary>
      {(financial: FinancialDataset) => {
        if (!onlyDiverged) return <ConciliacaoView financial={financial} />;
        // Recorte só das divergentes: mesmo dataset, seletor da fila.
        const filtered: FinancialDataset = {
          ...financial,
          reconciliations: financial.reconciliations.filter((row) => row.status === ("diverged" satisfies ReconciliationState))
        };
        return <ConciliacaoView financial={filtered} />;
      }}
    </FinancialBoundary>
  );
}
